import { constructApiUrl, sendRequest } from "../api/useFetch";

/**
 * Interface for marketplace offer acceptance request
 *
 * Marketplace API expects:
 * - Headers: x-user-id, x-user-roles, x-user-lsp-id, x-user-bd-id, Authorization (Bearer token)
 * - Body: { currency, price, price_minor_unit, type }
 * - Endpoint: POST /api/v1/chauffeur/offers/:offer_uuid/acceptance
 */
export interface MarketplaceAcceptOfferRequest {
  offer_uuid: string;
  currency: string;
  price: number;
  price_minor_unit: number;
  type: "prebooked" | "shift";
  user_id: string;
  user_roles: string[];
  user_lsp_id: string;
  user_bd_id: string;
}

/**
 * Marketplace error response structure
 */
export interface MarketplaceError {
  code: number;
  errorCode: string;
  message: string;
  cause?: string;
  fields?: MarketplaceErrorField[];
}

export interface MarketplaceErrorField {
  name: string;
  message: string;
  code: string;
}

/**
 * Marketplace success response (HTTP 200 without body)
 */
export interface MarketplaceAcceptOfferResponse {
  // Empty response on success
}

/**
 * Accept an offer through the marketplace API
 *
 * Endpoint: POST /api/v1/chauffeur/offers/:offer_uuid/acceptance
 * Headers: x-user-id, x-user-roles, x-user-lsp-id, x-user-bd-id, Authorization
 * Body: { currency, price, price_minor_unit, type }
 *
 * @param request - The marketplace offer acceptance request
 * @returns Promise resolving to empty response on success
 * @throws HttpResponseError with marketplace error details if the request fails
 */
export async function acceptMarketplaceOffer(
  request: MarketplaceAcceptOfferRequest,
): Promise<MarketplaceAcceptOfferResponse> {
  const path = `/api/v1/chauffeur/offers/${request.offer_uuid}/acceptance`;
  const url = constructApiUrl(path);

  // Prepare request body
  const body = {
    currency: request.currency,
    price: request.price,
    price_minor_unit: request.price_minor_unit,
    type: request.type,
  };

  // Prepare custom headers for marketplace API
  // Filter out "on_demand" role as per existing pattern
  const filteredRoles = request.user_roles.filter(
    (role) => role !== "on_demand",
  );

  const headers: Record<string, string> = {
    "x-user-id": request.user_id,
    "x-user-roles": filteredRoles.join(","),
    "x-user-lsp-id": request.user_lsp_id,
    "x-user-bd-id": request.user_bd_id,
  };

  // Add User-Agent header if available (client-side only)
  // Note: This only works in browser context (not during SSR/SSG)
  // During server-side rendering, navigator is undefined and this header is omitted
  if (typeof navigator !== "undefined" && navigator.userAgent) {
    headers["X-User-Agent"] = navigator.userAgent;
  }

  // sendRequest handles authentication (Bearer token) and throws HttpResponseError on failure
  await sendRequest(url, {
    method: "POST",
    body: JSON.stringify(body),
    headers,
  });

  // Success: HTTP 200 without body (or empty body)
  // No need to parse JSON for successful response
  return {};
}
